'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { LucideIcon } from '@skin-seoul/design-system';

/** Show controls only for directions with more products to scroll into view. */
export function CartHorizontalScroll({
  children,
  className,
  compact = false,
}: {
  children: ReactNode;
  className: string;
  compact?: boolean;
}) {
  const viewport = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: true, end: true });
  useEffect(() => {
    const element = viewport.current;
    if (!element) {
      return;
    }
    const update = () => {
      const next = {
        start: element.scrollLeft <= 1,
        end: element.scrollLeft + element.clientWidth >= element.scrollWidth - 1,
      };
      setEdges((previous) => (previous.start === next.start && previous.end === next.end ? previous : next));
    };
    const resizeObserver = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(update);
    const observeCards = () => Array.from(element.children).forEach((card) => resizeObserver?.observe(card));
    // Cards mount, unmount or change width (late data, image load) without the viewport resizing.
    const mutationObserver =
      typeof MutationObserver === 'undefined'
        ? undefined
        : new MutationObserver((records) => {
            records.forEach((record) => {
              record.removedNodes.forEach((node) => {
                if (node instanceof Element) {
                  resizeObserver?.unobserve(node);
                }
              });
            });
            observeCards();
            update();
          });
    update();
    element.addEventListener('scroll', update, { passive: true });
    resizeObserver?.observe(element);
    observeCards();
    mutationObserver?.observe(element, { childList: true });
    return () => {
      element.removeEventListener('scroll', update);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, []);
  const scroll = (direction: number) => {
    const element = viewport.current;
    if (!element) {
      return;
    }
    const left = element.getBoundingClientRect().left + element.clientLeft;
    const padding = parseFloat(getComputedStyle(element).paddingLeft) || 0;
    const positions = Array.from(element.children).map(
      (child) => child.getBoundingClientRect().left - left + element.scrollLeft - padding,
    );
    const target =
      direction > 0
        ? positions.find((position) => position > element.scrollLeft + 1)
        : [...positions].reverse().find((position) => position < element.scrollLeft - 1);
    if (target === undefined) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    element.scrollTo({
      left: Math.max(0, Math.min(target, element.scrollWidth - element.clientWidth)),
      behavior: reduced ? 'instant' : 'smooth',
    });
  };
  const arrowPosition = compact ? { previous: 'left-6', next: 'right-6' } : { previous: 'left-2', next: 'right-2' };
  return (
    <div className="relative">
      <div ref={viewport} className={className}>
        {children}
      </div>
      {(['previous', 'next'] as const)
        .filter((direction) => (direction === 'previous' ? !edges.start : !edges.end))
        .map((direction) => (
          <button
            key={direction}
            type="button"
            aria-label={`${direction === 'previous' ? 'Previous' : 'Next'} products`}
            onClick={() => scroll(direction === 'previous' ? -1 : 1)}
            className={`absolute z-10 flex size-7 cursor-pointer -translate-y-1/2 items-center justify-center rounded-full bg-white text-neutral-500 shadow-sm ${arrowPosition[direction]} ${compact ? 'top-1/2' : 'top-[76px]'}`}
          >
            <LucideIcon name={direction === 'previous' ? 'ChevronLeft' : 'ChevronRight'} size={16} />
          </button>
        ))}
    </div>
  );
}
